// YERİ: src/app/domains/website/sections/mkt-motion.ts
// Vitrinin hareket yardımcıları. Sıfır yeni bağımlılık: IntersectionObserver + rAF.
//
// SSR kuralı: her ikisi de sunucuda HİÇBİR ŞEY yapmaz ve içeriği DEĞİŞTİRMEZ.
// Sayaç sunucuda son değeri yazar, reveal sunucuda görünür kalır. Böylece
// sunucu HTML'i (crawler'ın gördüğü) her zaman tam ve okunabilir olur.

import { isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  Directive,
  ElementRef,
  inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';

/**
 * İlk çizimden sonra yerleşimin oturması için beklenen süre.
 *
 * Bölüm içeriği API'den asenkron geliyor: afterNextRender anında bazı
 * bölümlerin yüksekliği hâlâ 0 olabiliyor. Bu ikinci ölçüm o boşluğu kapatır.
 * setTimeout, IntersectionObserver'ın aksine arka plan sekmesinde de çalışır.
 */
const LAYOUT_SETTLE_MS = 700;

/**
 * Öğe görünür alana girdiğinde yumuşakça belirir.
 *
 * Başlangıçtaki gizlilik CSS'te `html.js .mkt-reveal` ile tanımlı — o sınıf
 * yalnız tarayıcıda eklendiği için JS'siz istemci hiçbir şey kaybetmez.
 */
@Directive({
  selector: '[mktReveal]',
  standalone: true,
  host: { class: 'mkt-reveal' },
})
export class MktReveal implements OnInit, OnDestroy {
  /** Sıralı görünüm için gecikme (ms) — ızgaradaki kartlarda index * 60 gibi. */
  readonly mktReveal = input<number | ''>('');

  private el = inject(ElementRef<HTMLElement>);
  private injector = inject(Injector);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private observer?: IntersectionObserver;
  private onVisibilityChange?: () => void;
  private checkNow?: () => void;
  private timer?: ReturnType<typeof setTimeout>;

  ngOnInit(): void {
    if (!this.isBrowser) return;

    const node = this.el.nativeElement as HTMLElement;

    // IntersectionObserver yoksa (çok eski tarayıcı) animasyonu atlayıp
    // içeriği hemen göster — asla gizli bırakma.
    if (typeof IntersectionObserver === 'undefined') {
      this.reveal(node);
      return;
    }

    const delay = Number(this.mktReveal()) || 0;
    if (delay > 0) node.style.transitionDelay = `${delay}ms`;

    // GÖZLEMCİYE GÜVENME, ÖLÇ.
    //
    // IntersectionObserver geri çağrıları tarayıcının "çizim" adımında
    // teslim edilir; sekme ARKA PLANDAYSA (document.hidden) o adım hiç
    // çalışmaz ve geri çağrı GELMEZ. Sayfa arka planda açılmışsa —
    // ctrl+tıkla yeni sekme, oturum geri yükleme, "bağlantıyı yeni sekmede
    // aç" — bölümler `opacity: 0` ile asılı kalıyordu: ziyaretçi sekmeye
    // geçtiğinde menü ve alt bilgi dışında BOŞ bir sayfa görüyordu.
    //
    // getBoundingClientRect arka planda da doğru çalışır. AMA ngOnInit'te
    // eleman henüz YERLEŞMEMİŞ olur (yükseklik 0) — ölçüm o an yapılırsa
    // "görünür alanda değil" der. O yüzden ölçüm ilk çizimden sonra
    // (afterNextRender) ve bir de gecikmeli olarak tekrarlanıyor: bölüm
    // içeriği API'den asenkron geliyor, boyu sonradan oluşuyor.
    this.checkNow = () => {
      if (this.observer === undefined && node.classList.contains('is-in')) return;
      if (this.isInViewport(node)) this.reveal(node);
    };

    afterNextRender(() => this.checkNow?.(), { injector: this.injector });
    this.timer = setTimeout(() => this.checkNow?.(), LAYOUT_SETTLE_MS);

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          // Tek seferlik — geri kaydırınca tekrar solmasın.
          this.reveal(node);
        }
      },
      // Öğe tam görünmeden biraz önce başlasın; alt kenardan %10 içeride tetikle.
      { threshold: 0.1, rootMargin: '0px 0px -10% 0px' },
    );

    this.observer.observe(node);

    // Sekme öne geldiğinde tekrar ölç: arka plandayken kaydırma konumu
    // değişmiş olabilir (oturum geri yükleme sayfayı eski konumuna atar) ve
    // gözlemci o değişimi görmemiştir.
    this.onVisibilityChange = () => {
      if (!document.hidden && this.isInViewport(node)) this.reveal(node);
    };
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  /** Gözlemcinin rootMargin'i ile AYNI mantık: alt kenardan %10 içeride. */
  private isInViewport(node: HTMLElement): boolean {
    const rect = node.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    return rect.top < viewportHeight * 0.9 && rect.bottom > 0;
  }

  private reveal(node: HTMLElement): void {
    node.classList.add('is-in');
    this.cleanup();
  }

  private cleanup(): void {
    this.observer?.disconnect();
    this.observer = undefined;

    if (this.timer !== undefined) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
    this.checkNow = undefined;

    if (this.onVisibilityChange) {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.onVisibilityChange = undefined;
    }
  }
}

/**
 * Sayıyı 0'dan hedefe animasyonla sayar (metrik bantları için).
 *
 * Sunucuda ve hareket-azaltma modunda doğrudan SON DEĞERİ yazar — bir metriğin
 * "0" olarak indekslenmesi kabul edilemez.
 */
@Directive({
  selector: '[mktCountUp]',
  standalone: true,
})
export class MktCountUp implements OnInit, OnDestroy {
  /** Hedef sayı. */
  readonly mktCountUp = input.required<number>();
  /** Sayının önüne/arkasına eklenecekler ("$", "%", "+", "K"). */
  readonly prefix = input<string>('');
  readonly suffix = input<string>('');
  /** Ondalık basamak — 4.9 gibi puanlar için. */
  readonly decimals = input<number>(0);
  readonly duration = input<number>(1600);

  private el = inject(ElementRef<HTMLElement>);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private observer?: IntersectionObserver;
  private frame?: number;

  ngOnInit(): void {
    const node = this.el.nativeElement as HTMLElement;
    const target = this.mktCountUp();

    const reducedMotion =
      this.isBrowser &&
      typeof matchMedia !== 'undefined' &&
      matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Sunucu, IO desteği olmayan tarayıcı, hareket-azaltma → son değeri yaz.
    if (!this.isBrowser || reducedMotion || typeof IntersectionObserver === 'undefined') {
      node.textContent = this.format(target);
      return;
    }

    node.textContent = this.format(0);

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.observer?.disconnect();
          this.animate(node, target);
        }
      },
      { threshold: 0.4 },
    );

    this.observer.observe(node);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    if (this.frame !== undefined) cancelAnimationFrame(this.frame);
  }

  private animate(node: HTMLElement, target: number): void {
    const duration = this.duration();
    const start = performance.now();

    const tick = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / duration);
      // easeOutCubic — hızlı başlayıp yumuşak duran, "sayaç" hissi veren eğri.
      const eased = 1 - Math.pow(1 - t, 3);

      node.textContent = this.format(target * eased);

      if (t < 1) {
        this.frame = requestAnimationFrame(tick);
      } else {
        // Yuvarlama hatası son değeri kaydırmasın.
        node.textContent = this.format(target);
      }
    };

    this.frame = requestAnimationFrame(tick);
  }

  private format(value: number): string {
    return `${this.prefix()}${value.toFixed(this.decimals())}${this.suffix()}`;
  }
}
